import { filterUndefineds } from '@olivierpascal/helpers';
import { useQueries } from 'react-query';
import { useDeepCompareMemo } from 'use-deep-compare';

import type { IApiGetPictureResponseBody } from '~/lib/api/modules/Pictures';
import { ITimeUnit } from '~/helpers/types';
import { useApi } from '~/lib/api';
import { IApiFileUploadStatus } from '~/lib/api/modules/Files';

export type IUsePicturesProps = {
  appId: string;
  ids: Array<string>;
  enabled?: boolean;
  refetchInterval?: number;
};

export type IUsePicturesResult = {
  isLoading: boolean;
  isProcessing: boolean;
  data: Array<IApiGetPictureResponseBody>;
};

export const useProcessedPictures = (
  props: IUsePicturesProps,
): IUsePicturesResult => {
  const { appId, ids, enabled, refetchInterval = 2 * ITimeUnit.SECOND } = props;

  const api = useApi();
  const queries = useQueries(
    ids?.map((id) => ({
      queryKey: [appId, 'picture', id],
      queryFn: () => api.pictures.get(appId, id),
      refetchInterval: (data: IApiGetPictureResponseBody | undefined) =>
        data?.status === IApiFileUploadStatus.READY ||
        data?.status === IApiFileUploadStatus.UPLOAD_ERROR ||
        data?.status === IApiFileUploadStatus.ENCODING_ERROR ||
        data?.status === IApiFileUploadStatus.ENCODING_JOB_ERROR
          ? false
          : refetchInterval,
      keepPreviousData: true,
      enabled,
    })) ?? [],
  );

  const isLoading = queries.some((query) => query.isLoading);
  const isProcessing = queries.some(
    (query) => query.data?.status !== IApiFileUploadStatus.READY,
  );
  const data = filterUndefineds(
    queries.map((query) =>
      query.data?.status !== IApiFileUploadStatus.READY
        ? undefined
        : query.data,
    ),
  );

  // Eeach data object inside the data array will be kept
  // referentially stable unless something changes inside of it, but the
  // reference to the array itself will change on every render. This is why we
  // need to use useDeepCompareMemo here to avoid infinite loops.
  // See:
  // - https://github.com/TanStack/query/discussions/5123#discussioncomment-5292543
  // - https://medium.com/@trisianto/react-query-how-to-memoize-results-from-usequeries-hook-eaed9a0ec700
  const stableData = useDeepCompareMemo(() => data, [data]);

  return {
    isLoading,
    isProcessing,
    data: stableData,
  };
};
